import React from 'react';
import PropTypes from 'prop-types';

import Stack, { StackProps as MuiStackProps } from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { List, ListItem, Typography } from '@mui/material';

export type ServiceTermsProps = MuiStackProps;

export const ServiceTerms = ({ className }: ServiceTermsProps) => {
  return (
    <Stack className={className} sx={{ maxWidth: '972px', mx: 'auto', gap: 2 }}>
      <Typography variant='body1'>
        These Service Terms are important so please read them carefully before you use the services
        on our website. They create a contract between you and us when you click “confirm order”.
      </Typography>
      <Typography variant='body1'>These Service terms apply</Typography>
      <Typography variant='body1'>
        When you create an account on this website at{' '}
        <a href='https://classity.co' style={{ textDecoration: 'underline' }}>
          www.classity.co
        </a>{' '}
        (“Website”) and use the Website for any of the following (“Services”):
      </Typography>
      <ul>
        <li>
          <Typography variant='body1'>
            Requesting and/or obtaining an online physician consultation (“Consultation”);
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            Requesting and/or obtaining a prescription to purchase any medication available through
            the Website (“Prescription”);
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            Requesting and/or obtaining any prescription medication available through the Website
            (“Prescription Product”); and
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            Purchase any other product, other than a Prescription Product, available through the
            Website (“Non-Prescription Product” and, with the Prescription Products, “Products”).
          </Typography>
        </li>
      </ul>
      <Typography variant='body1'>These Service Terms tell you:</Typography>
      <ol>
        <li>
          <Typography variant='body1'>How we will provide Services to you,</Typography>
        </li>
        <li>
          <Typography variant='body1'>
            How you and we may change or end the contract created between you and us
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>What to do if there is a problem and</Typography>
        </li>
        <li>
          <Typography variant='body1'>Other important information.</Typography>
        </li>
      </ol>
      <Typography variant='body1'>
        Please read these Service Terms carefully before you agree to them.
      </Typography>
      <Typography variant='body1'>
        If you think that there is a mistake in these terms please contact us to discuss.
      </Typography>
      <Typography variant='body1'>
        These Service Terms do not cover your purchase of any Consultation or Prescription Product.
      </Typography>
      <Typography variant='body1'>
        The Consultations and Prescription Products are sold by our our partner clinic Classity Care
        Clinic (“Clinic”).
      </Typography>
      <Typography variant='body1'>
        The Clinic’s terms and conditions of sale are set out in the Clinic Terms (below) which
        create a separate contract between you and the Clinic, regarding your purchase of the
        Consultations and any Prescription product
      </Typography>
      <Typography variant='body1'>
        <strong>1. Who we are, what we do and contacting us</strong>
      </Typography>
      <ol type='a'>
        <li>
          <Typography variant='body1'>
            We are Classity Edu Limited, company number CS082850321 and address at “we”, “us”.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            We offer a platform where you can create an user account (&quot;Account&quot;) which
            allows you to:
          </Typography>
          <ul>
            <li>
              <Typography variant='body1'>Obtain a Consultation;</Typography>
            </li>
            <li>
              <Typography variant='body1'>
                Purchase, on a monthly subscription basis (&quot;Subscription&quot;), Prescription
                Products, if any, that have been prescribed to you based on your Consultation; and
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                Purchase other Non-Prescription Products made available on the Website from time to
                time
              </Typography>
            </li>
          </ul>
        </li>
        <li>
          <Typography variant='body1'>
            We are a platform provider to help facilitate transactions that are carried out on the
            Website. We are neither the buyer nor the seller of the Consultations or the
            Prescription Products. The Consultations and Prescription Products are sold by the
            Clinic on the Clinic Terms.
          </Typography>
        </li>
      </ol>
      <Typography variant='body1'>
        <strong>2. Our Services</strong>
      </Typography>
      <ol type='a'>
        <li>
          <Typography variant='body1'>
            Through the Website, you can contract with us for the Services made available by us on
            the Website. However, as discussed in these Service Terms, your contract for the
            Consultations and the Prescription Products you subscribe to through the Services will
            be with the Clinic rather than with us.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            We are under a legal duty to provide Services that conform to their description on the
            Website and these Service Terms.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            We rely on you providing accurate information in order to provide the Services, so
            please ensure you answer our questions accurately. The Services are designed to help you
            access Consultations and, if prescribed by the physician, to buy the Prescription
            Products through a Subscription. To do this, we will ask questions about your health and
            current medication. Your answers are reviewed by a Ghanaian Medical and Dental Council
            Licensed Physician to determine whether it is safe to prescribe the Prescription Product
            to you. It is important you provide accurate responses to these questions, as you would
            to your doctor. In some cases, you may also need to answer additional questions to allow
            them to make this assessment, and we will contact you accordingly.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            To be able to access the Services you must first create an Account with us. You can
            register for and use the Services through our Website provided that you are at least
            eighteen (18) years old and a resident of the Ghana, but for safety reasons, you will
            only be able to purchase Prescription Products if you are between the ages of 18 and 65.
            You must provide all details that we request during the sign up process, including valid
            contact details and a valid payment method as permitted on the Website.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            Our website is solely for the promotion of our Services and the Products in Ghana.
            Unfortunately, we do not accept orders from or deliver to addresses outside Ghana.
          </Typography>
        </li>
      </ol>
      <Typography variant='body1'>
        <strong>2. Ordering</strong>
      </Typography>
      <ol type='a'>
        <li>
          <Typography variant='body1'>
            When you submit an order request for a Consultation and/or any Product (by clicking on
            the “Confirm Order” button), Company will send you an email with a reference number to
            confirm receipt of your order. The reference number will also be visible when you log in
            to your Account. It will help us if you can tell us this reference number whenever you
            contact us about your order.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            When you receive a reference number, you have entered a contract with us for the
            Services and any Non-Prescription Products on these Service Terms, and you have also
            entered a contract with the Clinic for the Consultation on the ClinicTerms. You have not
            entered into a contract for any Subscription for Prescription Products at this stage.
            You only have a contract with the Clinic for a Subscription if and when your physician
            approves a Prescription and we send you a further email stating that your physician has
            issued a Prescription for a Prescription Product. Note that when you click “Confirm
            Order”, payment is taken for your full order, including any requested Subscription, but
            if you are not approved for a Prescription Product, your payment, less any charged
            Consultation fee, will be refunded to you.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            The contract formed on entering into a Subscription for Prescription Products is solely
            between the Clinic and you. When you order a Consultation and/or a Subscription on the
            Website, you agree to the Clinic Terms are directly between you and Clinic. We are not a
            party to this contract and do not have any responsibility arising out of or in
            connection with it. We are not an agent for the Clinic. The Clinic are responsible to
            you for the sale and delivery of the Consultations and Prescription Products and for
            dealing with any claims or any other issues with the Consultations or Prescription
            Products.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            When we contact you, we will do so by telephone (including SMS) or by writing to your
            email address or location using the contact details provided in your Order. You must
            keep your contact details up-to- date in your Account so that Company can contact you
            about your order as necessary.
          </Typography>
        </li>
      </ol>
      <Typography variant='body1'>
        <strong>4.Your Subscription, cancelling and renewing</strong>
      </Typography>
      <ol type='a'>
        <li>
          <Typography variant='body1'>
            Once you subscribe, your Subscription will continue on a month-to-month basis until you
            cancel it. We will take payment using the payment details in your Account on the same
            date each month, starting from the date that your Prescription is issued.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            You may cancel or pause your Subscription at any time during your Subscription. If you
            cancel or pause your membership within 72 hours of your monthly billing date, we will
            still take payment for your next month’s Subscription. Your Subscription shall continue
            until the end of that month before being cancelled or paused.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            If you have cancelled your Subscription, you may not place a new order for the same
            Prescription Product within the same monthly Subscription period and must wait until the
            next monthly billing date has passed.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            From time to time we may contact you to verify that the information we have on file
            about your health and medication is still current and accurate and to ask you to notify
            us of any changes where it isn’t. In order to keep your Subscription active you must
            respond to any such request within the time period set out in the request.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            In order to keep your Subscription active and continue to receive Prescription Products,
            you must renew your Prescription every six months from the date of the Prescription. To
            renew your Prescription, you must log into your Account and complete a new Consultation.
            You will receive an email notifying you when you need to renew your Prescription.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            You can also find specific details of your Subscriptions by logging in to your Account.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>We may terminate or pause your Subscription if:</Typography>
          <ol type='i'>
            <li>
              <Typography variant='body1'>
                You have not renewed your Prescription in accordance with these Service Terms within
                six months of the when your Prescription was issued;
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                Your payment details or contact details are out of date/expired;
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                You have not confirmed that the information we have on file about your health and
                medication is still accurate (or updated your details if there has been a change)
                within the requested time period;
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                Your Account has been terminated or suspended (as set out below);
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                The requested Prescription Product is unavailable/out of stock;
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                We cannot authorise your payment for any reason; or
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                There has been a mistake on the pricing or the description of the Services.
              </Typography>
            </li>
          </ol>
        </li>
      </ol>
      <Typography variant='body1'>
        <strong>5. User Accounts</strong>
      </Typography>
      <ol type='a'>
        <li>
          <Typography variant='body1'>
            You must ensure that your username and password are kept secure and confidential. You
            are responsible for any activity on your Account. It is your sole responsibility to
            monitor and control access to and use of your Account. You agree to keep your
            information accurate, complete and current.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            You may make copies of your information contained in your account in any format and in
            doing so, you may make copies of reasonable portions of the Website and the material and
            content of the Website and your Account.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            We may suspend access to your account or terminate your Account at any time and for any
            reason. We will tell you as soon as possible if we suspend or terminate your Account.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            You can terminate these Service Terms at any time by contacting our customer services
            team. If you do so, you will no longer be able to access your Account or the information
            held in your Account and you will not be able to obtain any Consultations or Products.
          </Typography>
        </li>
      </ol>
      <Typography variant='body1'>
        <strong>6. Your Responsibilities</strong>
      </Typography>
      <ol type='a'>
        <li>
          <Typography variant='body1'>
            You are responsible for the information you provide to us about yourself, your medical
            conditions and your medication. You are responsible for letting us know if there are any
            changes in this information and for keeping it up to date. In particular, you need to
            tell us about any changes in your medical conditions, your medication and your delivery
            address. You promise that this information is accurate and acknowledge that if you do
            not provide accurate and up to date information you could be putting your health at
            serious risk. You can update your information by logging into your Account.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            You must review the patient information leaflet and follow the clinical directions
            provided with each Product before using it.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            You are responsible for notifying your doctors that you are using any Products.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            You agree that you will only order and use the Website, the Services and the Products
            for your own personal use. We do not offer the Website or the Services for you to use or
            order Consultations or Products on behalf of other people, including partners or other
            family members.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            You agree not to use our Website, the Services or any Products for any unlawful purpose.
            We will not be responsible for any misuse or abuse of the Website, Services or Products.
          </Typography>
        </li>
      </ol>
      <Typography variant='body1'>
        <strong>7. Our liability to you</strong>
      </Typography>
      <ol type='a'>
        <li>
          <Typography variant='body1'>
            These Service Terms do not exclude or limit our liability (if any) for:
          </Typography>
          <ol type='i'>
            <li>
              <Typography variant='body1'>
                death or personal injury caused by our negligence;
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>fraud;</Typography>
            </li>
            <li>
              <Typography variant='body1'>fraudulent misrepresentation; or</Typography>
            </li>
            <li>
              <Typography variant='body1'>
                any matter which it would be illegal for us to exclude or attempt to exclude our
                liability.
              </Typography>
            </li>
          </ol>
        </li>
        <li>
          <Typography variant='body1'>
            We are only liable to you for losses which you suffer as a direct result of our breach
            of these Service Terms and which are reasonably foreseeable. Loss or damage is
            foreseeable if either it is obvious that it will happen or if, at the time you accepted
            the Service Terms was made, both we and you knew it might happen.
          </Typography>
        </li>
      </ol>
      <Typography variant='body1'>
        <strong>8. Our Right to make changes to these Service Terms</strong>
      </Typography>
      <Typography variant='body1'>
        We may make changes to these Service Terms, but if we do so we will give you at least 10
        working days notice of any changes and you may then contact us to cancel your Subscription
        before the changes take effect and receive a refund for any Services or Products paid for
        but not received.
      </Typography>
      <Typography variant='body1'>
        <strong>9. Payments</strong>
      </Typography>
      <ol type='a'>
        <li>
          <Typography variant='body1'>
            All prices set out on the Website are inclusive of all taxes payable in the United
            Kingdom. The price of the Consultations and Products will be the price indicated on the
            page displaying your order when you clicked “Confirm Order”. We take all reasonable care
            to ensure that the prices displayed are correct.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>You may pay for Consultations and Products:</Typography>
          <ol type='i'>
            <li>
              <Typography variant='body1'>
                using your credit or debit card as indicated on the Website; or
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                using such e-wallets or e-money as are accepted on the Website from time to time.
              </Typography>
            </li>
          </ol>
        </li>
        <li>
          <Typography variant='body1'>
            You have to enter your chosen payment method and details when you open your Account. By
            completing your payment details, you confirm that the payment method being used belongs
            to you.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            All payments are subject to the approval of the financial institution issuing your
            credit/debit card and credit verification or the provider of e-money or mobile money,
            and we will not be responsible if the payment fails credit verification or if such
            financial institution or provider refuses to accept or honour the payment for any
            reason. We may also report any suspicious transactions to the relevant authorities. We
            may decline to accept or take any payment without giving any reason. Please note that it
            can take 2 to 3 days (excluding weekends and bank holidays) to process your payment. We
            will tell you if we incur any issues obtaining approval for the payment. This may cause
            a delay in delivering the Products to you and we will not be able to process your
            payment and deliver the Consultation or any Products until we have received
            authorisation.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            The payments you make include our fee for the Services and the price of the
            Consultations and Products and we will collect the relevant payment on behalf of the
            Clinic.
          </Typography>
        </li>
      </ol>
      <Typography variant='body1'>
        <strong>10. Delivery</strong>
      </Typography>
      <Typography variant='body1'>
        Delivery of the Prescription Products will only take place if your Prescription is approved
        and once the Subscription payment for that month has been processed. The delivery of the
        Prescription Products is governed by the Clinic Terms.
      </Typography>
      <Typography variant='body1'>
        <strong>11. Your personal information</strong>
      </Typography>
      <Typography variant='body1'>
        We take our responsibility for care of your personal information very seriously. Please see
        our Privacy Policy for information on how we process your personal information. Processing
        of your information by the Clinic will be governed by the Clinic Terms.
      </Typography>
      <Typography variant='body1'>
        <strong>12. Complaints and Customer Services</strong>
      </Typography>
      <ol type='a'>
        <li>
          <Typography variant='body1'>
            If you are unhappy with our service to you or any other matter then please contact us as
            soon as possible at: support@classity.co
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            Where your complaint is related to the provision and delivery of the Consultation,
            Prescription and Prescription Products or is medical in nature, we may redirect our
            query to the Clinic.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            We will try to resolve any disputes with you quickly and efficiently. If you and we
            cannot resolve a dispute using our internal complaint handling procedure, we will: let
            you know that we cannot settle the dispute with you; and give you certain information
            required by law about our alternative dispute resolution provider.
          </Typography>
        </li>
      </ol>
      <Typography variant='body1'>
        <strong>13. Disclaimers</strong>
      </Typography>
      <ol type='a'>
        <li>
          <Typography variant='body1'>
            The Services, and any Products provided through the Website, are provided on an “as is”
            and “as available” basis. WE MAKE NO REPRESENTATIONS, EXPRESS OR IMPLIED, AS TO THE
            SERVICES, THE PRODUCTS, OR ANY INFORMATION, CONTENT, OR MATERIALS INCLUDED IN OR
            REFERENCED ON THE WEBSITE. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL
            WARRANTIES AND CONDITIONS, EXPRESS OR IMPLIED, IN RESPECT OF THE PRODUCTS AND SERVICES
            CONTEMPLATED BY THESE SERVICE TERMS, INCLUDING, WITHOUT LIMITATION, IMPLIED WARRANTIES
            AND CONDITIONS OF MERCHANTABILITY ANDFITNESS FOR A PARTICULAR PURPOSE.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            We do our best to ensure that the information accessed through the Services is accurate
            and up to date but cannot guarantee that this will always be the case.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            We aim to make the Services available to you all the time but sometimes they may be
            unavailable due to maintenance or to factors outside of our control, such as the
            internet.
          </Typography>
        </li>
      </ol>
      <Typography variant='body1'>
        <strong>14. General</strong>
      </Typography>
      <ol type='a'>
        <li>
          <Typography variant='body1'>
            These Service Terms and all communications between us will be in English. No other
            languages will apply to these Service Terms.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            You may not transfer any of your rights under these Service Terms to any other person.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            We may transfer any of our rights under these Service Terms to any person or ask any
            person to fulfil any aspect of it so long as the performance of these Service Terms is
            not affected.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            Only you and us have any rights under these Service Terms. No other person shall have
            any rights under these Service Terms.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            Each of the paragraphs of these Service Terms operates separately. If any court or
            relevant authority decides that any of them are unlawful, the remaining paragraphs will
            remain in full force and effect.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            Even if we delay in enforcing these Service Terms we can still enforce it later. For
            example, if you miss a payment and we do not chase you but we continue to provide the
            products, we can still require you to make the payment at a later date.
          </Typography>
        </li>
        <li>
          <Typography variant='body1'>
            These Service Terms will be governed by Ghanaian law and you may bring legal proceedings
            in respect of these Service Terms and/or the supply of services by us in the Ghana
            Courts.
          </Typography>
        </li>
      </ol>
      <Typography variant='body1'>Have a question?</Typography>
      <a href='mailto:support@classity.co'>
        <Typography variant='body1'>support@classity.co</Typography>
      </a>
      <Typography variant='body1'>Press inquiries?</Typography>
      <a href='mailto:press@classity.co'>
        <Typography variant='body1'>press@classity.co</Typography>
      </a>
      <Typography variant='body1'>*all photos are models and not actual patients.</Typography>
      <Typography variant='body1'>User Terms</Typography>
      <Typography variant='body1'>Service Terms</Typography>
      <Typography variant='body1'>Privacy Policy</Typography>
      <Typography variant='body1'>Clinic Terms</Typography>
      <Typography variant='body1'>Copyright 2024 CLASSITY EDU. All rights reserved.</Typography>
    </Stack>
  );
};

ServiceTerms.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ServiceTerms.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(ServiceTerms)(
  ({ theme }) => `
  & {
    background-color: ${theme.palette.background.paper};
  }
  
  & .MuiTypography-body1  {
    font-family: ${theme.typography.gillSans.regular};
  }

  & a {
    text-decoration: none;
    color: ${theme.palette.primary.main};
  }

  & ul, & ol {
    margin-top: 0;
    margin-bottom: 0;

    & li {
      margin-bottom: 8px;
    }

    & li::marker {
      font-weight: 600;
      font-size: 1rem
    }
  }
`,
);
